/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useState, useCallback, useEffect, useMemo,
} from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import { updatePathWithTabs } from "../../utils/Routes";
import Customers from "../Components/Dashboard/Customers";
import Activity from "../Components/Dashboard/Activity/Activity";
import DashboardInstrument from "../Components/Dashboard/Instrument/DashboardInstrument";
import DashboardMlRouting from "../Components/Dashboard/MLRouting/DashboardMlRouting";
import DashboardEsvRetestActions from "../Components/Dashboard/DashboardEsvRetestActions";
import { getUser } from "../../utils/helpers";

const AntTabs = withStyles({
  root: {
    backgroundColor: "#F4F5F7",
    borderBottom: "1px solid #e8e8e8",
  },
  indicator: {
    backgroundColor: "#494B83",
  },
})(Tabs);

const AntTab = withStyles((theme) => ({
  root: {
    textTransform: "none",
    minWidth: 160,
    fontWeight: theme.typography.fontWeightRegular,
    marginRight: theme.spacing(4),
    fontSize: "14px",
    lineHeight: "24px",
    color: "#6B778C",
    fontFamily: ["Roboto Bold"].join(","),
    "&:hover": {
      color: "#494B83",
      opacity: 1,
    },
    "&$selected": {
      color: "#494B83",
      fontWeight: theme.typography.fontWeightMedium,
    },
    "&:focus": {
      color: "#494B83",
    },
  },
  selected: {},
}))((props) => <Tab disableRipple {...props} />);

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  padding: {
    padding: theme.spacing(3),
  },
  dashboard: {
    backgroundColor: "#F4F5F7",
  },
}));

function TabPanel(props) {
  const {
    children, value, index, ...other
  } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && <div>{children}</div>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

TabPanel.defaultProps = {
  children: null,
};

function a11yProps(index) {
  return {
    id: `dashboard-tab-${index}`,
    "aria-controls": `dashboard-tabpanel-${index}`,
  };
}

export default function DashboardTab() {
  const classes = useStyles();
  const [value, setValue] = useState(0);
  // Variables used for updating the path
  const history = useHistory();
  const page = "dashboard";
  const index = undefined;
  const currentPath = undefined;
  const user = useMemo(() => getUser(), []);
  const tabs = useMemo(() => [
    {
      tabLabel: "Customers", route: "customers", permission: "view_automation_dashboard_customer", component: <Customers />,
    },
    {
      tabLabel: "Activity", route: "activity", permission: "view_automation_dashboard_activity", component: <Activity />,
    },
    {
      tabLabel: "Instrument", route: "instrument", permission: "view_automation_dashboard_instrument", component: <DashboardInstrument />,
    },
    {
      tabLabel: "ML Routing", route: "ml-routing", permission: "view_automation_dashboard_ml_routing", component: <DashboardMlRouting />,
    },
    {
      tabLabel: "ESV Actions", route: "esv-retest-actions", permission: "view_automation_dashboard_esv_actions", component: <DashboardEsvRetestActions />,
    },
  ], []);
  const authorizedTabs = useMemo(() => tabs.filter((item) => user.permissionMap[item.permission] || item.permission === "no-permission").map((item, i) => ({ ...item, tabValue: i })), [tabs, user]);

  useEffect(() => {
    const path = window.location.pathname;
    // On refresh, makes sure the state is set correctly
    updatePathWithTabs(path, index, page, setValue, authorizedTabs, history);

    // Handles the case when the back button on the browser is clicked
    const unlisten = history.listen((location, action) => {
      if (action === "POP") {
        updatePathWithTabs(location.pathname, index, page, setValue, authorizedTabs, history);
      }
    });

    // Cleanup subscription on component unmount
    return () => {
      unlisten();
    };
  }, []); // eslint-disable-line

  const handleChange = useCallback((event, newValue) => {
    // Depending on the value of the tab, update the path
    updatePathWithTabs(currentPath, newValue, page, setValue, authorizedTabs, history);

    // Update the state
    setValue(newValue);
  }, [history, currentPath, authorizedTabs]);

  return (
    <div className={classes.root}>
      <div className={classes.dashboard}>
        <AntTabs value={value} onChange={handleChange} aria-label="ant tabs">
          {authorizedTabs.map((item) => (<AntTab key={item.tabValue} label={item.tabLabel} {...a11yProps(item.tabValue)} />))}
        </AntTabs>
        {authorizedTabs.map((item) => (
          <TabPanel key={item.tabValue} value={value} index={item.tabValue}>
            {item.component}
          </TabPanel>
        ))}
      </div>
    </div>
  );
}
